/* global setTimeout, clearTimeout */
/**
 * MyDayBriefing.js
 *
 * Global daily "Mai napom" (My day) briefing. Mounted ONCE in `AppNavigator` (inside the NavigationContainer and the
 * System provider, above every module's screens), so it appears in whatever app module is active. Opens
 * automatically on the first launch of each local day and on the user's choices:
 *
 * - Értesítsen újra   -> snooze (15 min / 1 h / 3 h): in-app timer + a local notification at that time
 * - Következő induláskor -> reopen on the next cold start or a foreground return after >= 10 min in the background
 * - Kihagyás ma        -> no auto-open for the rest of the local day, pending reminder cancelled
 * - X / back           -> seen for today
 *
 * The decision is the pure {@link module:helpers/myDayBriefing.resolveAutoOpen}; state is persisted per user in
 * AsyncStorage ({@link module:helpers/myDayStore}). Never throws, never blocks the UI.
 *
 * Munkanap (workday) sequencing: the briefing HOSTS the start-of-day prompt (its modal carries the workday hero card). It
 * reports every auto-open decision to the WorkdayProvider (`reportBriefingDecision`) and its visibility
 * (`setBriefingVisible`); the provider then shows the compact standalone start sheet only when the briefing will NOT open
 * today (`skipped` / `seen`) and never while the briefing is visible ({@link module:helpers/workdayRules.pickSurface}).
 *
 * @module components/management/MyDayBriefing
 */

import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {AppState, Platform} from 'react-native';
import {useTranslation} from 'react-i18next';
import MyDayBriefingModal from './MyDayBriefingModal';
import crossToast from '../../helpers/crossToast';
import {navigationRef} from '../../helpers/navigationRef';
import {SystemContext} from '../../helpers/SystemContext';
import {TourContext} from '../../helpers/TourContext';
import {getUserInfo} from '../../helpers/storage';
import {describeApiError} from '../../helpers/apiError';
import {hasNotificationLaunchIntent} from '../../helpers/notificationLaunchIntent';
import {fetchAgenda, localDayKey, normalizeAgenda} from '../../helpers/salesFollowUpApi';
import {fetchFocus} from '../../helpers/myDayFocusApi';
import {normalizeFocus} from '../../helpers/myDayFocus';
import {isRouteAllowed, requestMyDayOpen, resolveAutoOpen, snoozeDueAt, subscribeMyDayOpen} from '../../helpers/myDayBriefing';
import {loadMyDayState, markOpened, saveHidden, saveNextLaunch, saveSnooze} from '../../helpers/myDayStore';
import {cancelMyDayReminder, scheduleMyDayReminder} from '../../helpers/myDayReminder';
import {workItemRoute} from '../../helpers/myDayWorkItems';
import {SALES_FOLLOW_UPS_ROUTE} from '../../config/salesRoutes';
import {useWorkday} from '../../helpers/WorkdayContext';

export {requestMyDayOpen};

const ROUTE_RETRY_MS = 1500;
const ROUTE_RETRIES = 12;
const TICK_MS = 30 * 1000;
/** The in-app timer fires slightly early so it wins over (and cancels) the identical OS notification. */
const EARLY_MS = 1200;

/** First evaluation of this JS process = cold start (module-level so a re-login in the same process is not "cold"). */
let coldPending = true;

const fmtClock = (ms) => new Date(ms).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});

/** @returns {boolean} whether the root navigator declares `name` (so navigating there cannot throw). */
const rootHas = (name) => {
    try {
        return navigationRef.isReady() && !!navigationRef.getRootState()?.routeNames?.includes(name);
    } catch (_e) {
        return false;
    }
};

/**
 * @param {{enabled: boolean}} props `enabled` — the user is logged in.
 * @returns {JSX.Element|null} the briefing modal (rendered only while open).
 */
export default function MyDayBriefing({enabled}) {
    const {t} = useTranslation();
    const system = useContext(SystemContext);
    const tour = useContext(TourContext);
    const restaurant = system?.restaurant;
    const restaurantId = String(restaurant?._id || restaurant?.id || '');
    const tourActive = !!tour?.activeAppTutorialStep;
    const {reportBriefingDecision, setBriefingVisible} = useWorkday();

    const [userId, setUserId] = useState('');
    const [visible, setVisible] = useState(false);
    const [phase, setPhase] = useState('loading');
    const [agenda, setAgenda] = useState(() => normalizeAgenda(null));
    const [focus, setFocus] = useState(() => normalizeFocus(null));
    const [error, setError] = useState('');

    const busy = useRef(false);
    const visibleRef = useRef(false);
    const timer = useRef(null);
    const retryTimer = useRef(null);
    const backgroundedAt = useRef(0);
    const userRef = useRef('');
    const tourRef = useRef(false);

    userRef.current = userId;
    tourRef.current = tourActive;

    const ready = !!enabled && !!userId && !!restaurantId;

    const clearTimers = useCallback(() => {
        clearTimeout(timer.current);
        clearTimeout(retryTimer.current);
        timer.current = null;
        retryTimer.current = null;
    }, []);

    const hide = useCallback(() => {
        visibleRef.current = false;
        setVisible(false);
    }, []);

    const load = useCallback(async () => {
        setPhase('loading');
        setError('');

        try {
            // "My day" is personal: scope the agenda to the signed-in user (managers would otherwise get the whole team's follow-ups).
            // The focus block is additive: an old server / a failure leaves it empty and never blocks the briefing.
            const [nextAgenda, nextFocus] = await Promise.all([fetchAgenda({upcomingDays: 3, userId: userRef.current}), fetchFocus()]);

            setAgenda(nextAgenda);
            setFocus(nextFocus);
            setPhase('ready');
        } catch (err) {
            setError(describeApiError(err, t('myday_error_text')));
            setPhase('error');
        }
    }, [t]);

    const openBriefing = useCallback(async () => {
        const uid = userRef.current;

        if (!uid || visibleRef.current) {
            return;
        }

        visibleRef.current = true;
        clearTimers();
        setVisible(true);
        // The briefing hosts the Munkanap start prompt: the standalone start sheet must not follow it.
        reportBriefingDecision({open: true, reason: 'opened'});
        // Mark first: a crash / failed fetch must never re-open the briefing in a loop.
        markOpened(uid, localDayKey());
        cancelMyDayReminder(uid);
        load();
    }, [clearTimers, load, reportBriefingDecision]);

    const evaluateRef = useRef(null);

    const armTimer = useCallback((dueAt) => {
        clearTimeout(timer.current);

        const remaining = dueAt - Date.now();
        const wait = remaining > TICK_MS + EARLY_MS ? TICK_MS : Math.max(remaining - EARLY_MS, 300);

        timer.current = setTimeout(() => {
            if (Date.now() < dueAt - EARLY_MS - 400) {
                armTimer(dueAt);

                return;
            }

            if (AppState.currentState === 'active') {
                // The timer won the race: drop the OS notification so the user is not told twice.
                cancelMyDayReminder(userRef.current);
                evaluateRef.current?.('foreground', 0, dueAt, 0);
            }
        }, wait);
    }, []);

    const evaluate = useCallback(async (trigger, backgroundMs = 0, nowMs = Date.now(), attempt = 0) => {
        const uid = userRef.current;

        if (!uid || busy.current || visibleRef.current) {
            return;
        }

        busy.current = true;

        try {
            const route = navigationRef.isReady() ? navigationRef.getCurrentRoute()?.name : '';
            const routeOk = isRouteAllowed(route) && !tourRef.current;
            const state = await loadMyDayState(uid, localDayKey());
            const decision = resolveAutoOpen({ready: routeOk, ...state, trigger, backgroundMs, now: nowMs});

            if (decision.reason === 'not-ready') {
                // Login / lock / 2FA / start screen or the app tour: look again shortly.
                if (attempt < ROUTE_RETRIES) {
                    clearTimeout(retryTimer.current);
                    retryTimer.current = setTimeout(() => evaluateRef.current?.(trigger, backgroundMs, Date.now(), attempt + 1), ROUTE_RETRY_MS);
                }

                return;
            }

            const wasCold = trigger === 'cold';

            if (wasCold) {
                coldPending = false;
            }

            if (decision.reason === 'snoozed') {
                reportBriefingDecision(decision);
                armTimer(state.snoozeUntil);

                return;
            }

            if (!decision.open) {
                // skipped / seen -> the briefing will not open now: the workday start prompt may stand alone.
                reportBriefingDecision(decision);

                return;
            }

            // A push-tap cold start navigates to its target first; the briefing then waits for the next foreground.
            if (wasCold && decision.reason !== 'snooze-due' && hasNotificationLaunchIntent()) {
                reportBriefingDecision({open: false, reason: 'deferred'});

                return;
            }

            await openBriefing();
        } catch (_e) {
            // best effort only
        } finally {
            busy.current = false;
        }
    }, [armTimer, openBriefing, reportBriefingDecision]);

    evaluateRef.current = evaluate;

    // Resolve the signed-in user; tear everything down on logout.
    useEffect(() => {
        let cancelled = false;

        if (!enabled) {
            clearTimers();
            hide();
            cancelMyDayReminder(userRef.current);
            setUserId('');
            reportBriefingDecision(null);

            return undefined;
        }

        getUserInfo().then((u) => {
            if (!cancelled) {
                setUserId(String(u?._id || u?.id || ''));
            }
        }).catch(() => {});

        return () => { cancelled = true; };
    }, [enabled, clearTimers, hide, reportBriefingDecision]);

    // Tell the workday provider whether the briefing is on screen (it outranks every other workday surface).
    useEffect(() => {
        setBriefingVisible(visible);

        return () => setBriefingVisible(false);
    }, [visible, setBriefingVisible]);

    // No store resolved for a while = the briefing cannot open (no access / not set up): let the workday start prompt stand alone.
    useEffect(() => {
        if (!enabled || !userId || restaurantId) {
            return undefined;
        }

        const id = setTimeout(() => reportBriefingDecision({open: false, reason: 'skipped'}), 4000);

        return () => clearTimeout(id);
    }, [enabled, userId, restaurantId, reportBriefingDecision]);

    // Startup / login evaluation + every return to the foreground.
    useEffect(() => {
        if (!ready) {
            return undefined;
        }

        evaluate(coldPending ? 'cold' : 'foreground');

        const sub = AppState.addEventListener('change', (state) => {
            if (state === 'background') {
                backgroundedAt.current = Date.now();
            } else if (state === 'active') {
                const away = backgroundedAt.current ? Date.now() - backgroundedAt.current : 0;

                backgroundedAt.current = 0;
                evaluate('foreground', away);
            }
        });

        return () => {
            sub?.remove?.();
            clearTimers();
        };
    }, [ready, evaluate, clearTimers]);

    // Manual entry point (Teendők screen).
    useEffect(() => subscribeMyDayOpen(() => {
        if (userRef.current && !visibleRef.current) {
            openBriefing();
        }
    }), [openBriefing]);

    /* ------------------------------ footer choices ------------------------------ */

    const toast = useCallback((message) => {
        try {
            crossToast.show(message, 2500);
        } catch (_e) {
            // toast host unavailable
        }
    }, []);

    const onSnooze = useCallback(async (minutes) => {
        const uid = userRef.current;
        const dueAt = snoozeDueAt(minutes);

        hide();

        if (!uid || !dueAt) {
            return;
        }

        await saveSnooze(uid, dueAt);
        // Web has no local notifications: the in-app timer alone carries the reminder.
        if (Platform.OS !== 'web') {
            await scheduleMyDayReminder(uid, dueAt, {title: t('myday_notif_title'), body: t('myday_notif_body')});
        }

        armTimer(dueAt);
        toast(t('myday_toast_snoozed', {time: fmtClock(dueAt)}));
    }, [armTimer, hide, t, toast]);

    const onNextLaunch = useCallback(async () => {
        const uid = userRef.current;

        hide();
        clearTimers();

        if (uid) {
            await saveNextLaunch(uid);
            await cancelMyDayReminder(uid);
        }

        toast(t('myday_toast_next_launch'));
    }, [clearTimers, hide, t, toast]);

    const onSkipToday = useCallback(async () => {
        const uid = userRef.current;

        hide();
        clearTimers();

        if (uid) {
            await saveHidden(uid, localDayKey());
            await cancelMyDayReminder(uid);
        }

        toast(t('myday_toast_skipped'));
    }, [clearTimers, hide, t, toast]);

    const currentRoute = visible && navigationRef.isReady() ? navigationRef.getCurrentRoute()?.name : '';
    const canOpenAgenda = visible && rootHas(SALES_FOLLOW_UPS_ROUTE) && currentRoute !== SALES_FOLLOW_UPS_ROUTE;

    const onOpenAgenda = useCallback(() => {
        hide();

        try {
            navigationRef.navigate(SALES_FOLLOW_UPS_ROUTE);
        } catch (_e) {
            // route unavailable in this build
        }
    }, [hide]);

    const onOpenWorkItem = useCallback((item) => {
        const target = workItemRoute(item);

        if (!target || !rootHas(target.name)) {
            return;
        }

        hide();

        try {
            navigationRef.navigate(target.name, target.params);
        } catch (_e) {
            // route unavailable in this build
        }
    }, [hide]);

    const onNavigateTarget = useCallback((target) => {
        if (!target || !rootHas(target.name)) {
            return;
        }

        hide();

        try {
            navigationRef.navigate(target.name, target.params);
        } catch (_e) {
            // route unavailable in this build
        }
    }, [hide]);

    if (!visible) {
        return null;
    }

    return (
        <MyDayBriefingModal
            visible={visible}
            phase={phase}
            agenda={agenda}
            focus={focus}
            error={error}
            canOpenAgenda={canOpenAgenda}
            onClose={hide}
            onRetry={load}
            onOpenAgenda={onOpenAgenda}
            onSnooze={onSnooze}
            onNextLaunch={onNextLaunch}
            onSkipToday={onSkipToday}
            onOpenWorkItem={onOpenWorkItem}
            onNavigate={onNavigateTarget}
        />
    );
}
